import { useTwilioDevice } from "./useTwilioDevice";
import { useCallOperations } from "./useCallOperations";
import { useConferenceOperations } from "./useConferenceOperations";
import { useCallingStore } from "../../store";
import { useMemo } from "react";

/**
 * Main composite hook for calling functionality.
 *
 * Combines device management, call operations, and conference/transfer operations
 * into a single convenient interface.
 *
 * Usage:
 * ```tsx
 * function CallUI() {
 *   const calling = useCalling();
 *
 *   // Initialize device on mount
 *   useEffect(() => {
 *     calling.initializeDevice();
 *   }, []);
 *
 *   // Make a call
 *   const handleCall = () => {
 *     calling.makeCall('+15551234567', { enduserId: '...' });
 *   };
 *
 *   // Answer incoming call
 *   const handleAnswer = (call) => {
 *     calling.answerCall(call);
 *   };
 *
 *   return (
 *     <div>
 *       {calling.isConnected ? 'Ready' : 'Connecting...'}
 *       {calling.incomingCalls.length > 0 && 'Incoming call!'}
 *     </div>
 *   );
 * }
 * ```
 */
export function useCalling() {
  const device = useTwilioDevice();
  const operations = useCallOperations();
  const conference = useConferenceOperations();
  const callingStore = useCallingStore();

  return useMemo(
    () => ({
      // ===== STATE =====

      // Device state
      isConnected: device.isConnected,
      isInitialized: device.isInitialized,
      isInitializing: device.isInitializing,
      deviceState: device.deviceState,
      connectionError: device.connectionError,

      // Call state
      activeCall: operations.activeCall,
      callStatus: operations.callStatus,
      callDirection: operations.callDirection,
      isMuted: operations.isMuted,
      activeCallMeta: operations.activeCallMeta,

      // Incoming calls
      incomingCalls: operations.incomingCalls,
      hasIncomingCalls: operations.incomingCalls.length > 0,

      // Conference state
      conference: conference.conference,
      isInConference: conference.isInConference,

      // Warm transfer state
      warmTransfer: conference.warmTransfer,
      isWarmTransferActive: conference.isWarmTransferActive,

      // ===== DEVICE OPERATIONS =====
      initializeDevice: device.initializeDevice,
      reconnect: device.reconnect,
      destroyDevice: device.destroyDevice,
      refreshToken: device.refreshToken,

      // ===== CALL OPERATIONS =====
      makeCall: operations.makeCall,
      answerCall: operations.answerCall,
      rejectCall: operations.rejectCall,
      ignoreCall: operations.ignoreCall,
      hangUp: operations.hangUp,

      // ===== CALL CONTROL =====
      toggleMute: operations.toggleMute,
      sendDigits: operations.sendDigits,
      getCallStatusLabel: operations.getCallStatusLabel,

      // ===== CONFERENCE OPERATIONS =====
      upgradeToConference: conference.upgradeToConference,
      addAttendeeByUser: conference.addAttendeeByUser,
      addAttendeeByPhone: conference.addAttendeeByPhone,
      removeAttendee: conference.removeAttendee,
      endConference: conference.endConference,

      // ===== WARM TRANSFER OPERATIONS =====
      initiateWarmTransfer: conference.initiateWarmTransfer,
      completeWarmTransfer: conference.completeWarmTransfer,
      cancelWarmTransfer: conference.cancelWarmTransfer,

      // ===== RAW STORE ACCESS =====
      // For advanced use cases where direct store manipulation is needed
      store: callingStore,
    }),
    [
      callingStore,
      conference.addAttendeeByPhone,
      conference.addAttendeeByUser,
      conference.cancelWarmTransfer,
      conference.completeWarmTransfer,
      conference.conference,
      conference.endConference,
      conference.initiateWarmTransfer,
      conference.isInConference,
      conference.isWarmTransferActive,
      conference.removeAttendee,
      conference.upgradeToConference,
      conference.warmTransfer,
      device.connectionError,
      device.destroyDevice,
      device.deviceState,
      device.initializeDevice,
      device.isConnected,
      device.isInitialized,
      device.isInitializing,
      device.reconnect,
      device.refreshToken,
      operations.activeCall,
      operations.activeCallMeta,
      operations.answerCall,
      operations.callDirection,
      operations.callStatus,
      operations.getCallStatusLabel,
      operations.hangUp,
      operations.ignoreCall,
      operations.incomingCalls,
      operations.isMuted,
      operations.makeCall,
      operations.rejectCall,
      operations.sendDigits,
      operations.toggleMute,
    ]
  );
}

/**
 * Type for the return value of useCalling hook.
 * Useful for typing components that receive calling functionality as props.
 */
export type UseCallingReturn = ReturnType<typeof useCalling>;
